import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { DriftImage, PipelineStage, PipelineRunInfo, PromotionRecord } from '../api'

const runBadge = (run: PipelineRunInfo) => {
    if (run.state === 'inProgress') {
        return { bg: 'bg-sky-500/15 ring-1 ring-sky-500/30', text: 'text-sky-400', label: 'In progress' }
    }
    if (run.result === 'succeeded') {
        return { bg: 'bg-emerald-500/15 ring-1 ring-emerald-500/30', text: 'text-emerald-400', label: 'Succeeded' }
    }
    if (run.result === 'failed') {
        return { bg: 'bg-red-500/15 ring-1 ring-red-500/30', text: 'text-red-400', label: 'Failed' }
    }
    if (run.result === 'canceled') {
        return { bg: 'bg-slate-500/15 ring-1 ring-slate-500/30', text: 'text-slate-400', label: 'Canceled' }
    }
    return { bg: 'bg-slate-500/15 ring-1 ring-slate-500/30', text: 'text-slate-400', label: run.state }
}

const relativeTime = (iso: string | null): string => {
    if (!iso) return '—'
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60_000)
    if (mins < 1)  return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24)  return `${hrs}h ago`
    return `${Math.floor(hrs / 24)}d ago`
}
import { StageCard } from '../components/StageCard'
import { PromoteArrow } from '../components/PromoteArrow'

export function PipelinePage() {
    const { data, isLoading, isError, error, dataUpdatedAt, refetch, isFetching } = useQuery({
        queryKey: ['pipeline'],
        queryFn: releaseApi.getPipeline,
        refetchInterval: 60_000,
    })

    // Track which arrows are mid-promotion so the adjacent cards pulse.
    const [promotingIndex, setPromotingIndex] = useState<number | null>(null)

    const stages = data?.stages ?? []

    return (
        <>
            <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-slate-200">Release Pipeline</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                        {dataUpdatedAt > 0 && (
                            <span>
                                Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
                            </span>
                        )}
                        <button
                            onClick={() => void refetch()}
                            disabled={isFetching}
                            className="rounded-lg border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors disabled:opacity-40"
                        >
                            {isFetching ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
                {/* Legend */}
                <div className="flex items-center gap-6 mb-10 text-xs text-slate-400">
                    <span className="font-semibold text-slate-200 text-sm">Release Flow</span>
                    <div className="flex items-center gap-1.5">
                        <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500/60" />
                        synced with previous ring
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-500/60" />
                        ahead — promotion available
                    </div>
                </div>

                {/* Pipeline diagram */}
                {isLoading && <PipelineSkeleton />}

                {isError && (
                    <div className="rounded-xl border border-red-800 bg-red-950/50 p-6 text-red-300 text-sm">
                        Failed to load pipeline state:{' '}
                        {error instanceof Error ? error.message : 'Unknown error'}
                    </div>
                )}

                {!isLoading && !isError && (
                    <div className="flex items-start gap-0 overflow-x-auto pb-6">
                        {stages.map((stage, i) => {
                            const prev = stages[i - 1]
                            const isPromoting = promotingIndex === i || promotingIndex === i - 1
                            return (
                                <div key={stage.name} className="flex items-center">
                                    <StageCard
                                        stage={stage}
                                        prevStage={prev}
                                        isPromoting={isPromoting}
                                        animateIn={true}
                                    />
                                    {i < stages.length - 1 && (
                                        <PromoteArrow
                                            source={stage}
                                            target={stages[i + 1]!}
                                            onPromoting={(p) => setPromotingIndex(p ? i : null)}
                                        />
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}

                {/* Tag reference table */}
                {stages.length > 0 && (
                    <TagTable stages={stages} />
                )}

                {/* Promotion audit trail + rollback (AB#2092) */}
                <PromotionHistoryPanel />

                {/* CI/CD build status */}
                <CiBuildPanel />
            </main>
        </>
    )
}

// AB#2094 — per-cell drift dot: does the cluster actually run what the overlay declares?
function DriftDot({ drift }: { drift: DriftImage | undefined }) {
    if (!drift || drift.state === 'unknown') {
        return null
    }
    const cls = drift.state === 'synced' ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'
    const title = drift.state === 'synced'
        ? 'Cluster runs the declared tag'
        : `Cluster runs ${drift.runningTags.join(', ')} — overlay declares ${drift.gitopsTag}`
    return (
        <span
            title={title}
            className={`ml-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle ${cls}`}
        />
    )
}

function TagTable({ stages }: { stages: PipelineStage[] }) {
    const imageNames = Object.keys(stages[0]?.tags ?? {}).filter(n => !n.endsWith('-migrations'))

    // AB#2094 — desired-vs-running join; unknown envs simply render no dot.
    const { data: drift } = useQuery({
        queryKey: ['pipeline-drift'],
        queryFn: releaseApi.getDrift,
        refetchInterval: 60_000,
        staleTime: 30_000,
    })
    const driftFor = (env: string, img: string): DriftImage | undefined =>
        drift?.envs.find(e => e.env === env)?.images.find(i => i.name === img)

    return (
        <div className="mt-12 rounded-xl border border-slate-800 overflow-hidden">
            <div className="bg-slate-900 px-4 py-3 flex items-center gap-4">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Full tag reference
                </span>
                <span className="flex items-center gap-1.5 text-[10px] text-slate-500">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    running matches gitops
                </span>
                <span className="flex items-center gap-1.5 text-[10px] text-slate-500">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
                    cluster behind overlay
                </span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="border-b border-slate-800">
                            <th className="py-2 px-4 text-left text-slate-500 font-medium">Image</th>
                            {stages.map(s => (
                                <th key={s.name} className="py-2 px-4 text-left text-slate-400 font-semibold">
                                    {s.displayName}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {imageNames.map((img, rowIdx) => (
                            <tr
                                key={img}
                                className={rowIdx % 2 === 0 ? 'bg-slate-900/40' : ''}
                            >
                                <td className="py-2 px-4 font-mono text-slate-400">{img}</td>
                                {stages.map((s, colIdx) => {
                                    const tag = s.tags[img] ?? '—'
                                    const prevTag = stages[colIdx - 1]?.tags[img]
                                    const synced = prevTag !== undefined && tag === prevTag
                                    return (
                                        <td
                                            key={s.name}
                                            className={`py-2 px-4 font-mono whitespace-nowrap ${synced ? 'text-emerald-400' : colIdx > 0 ? 'text-amber-400' : 'text-slate-300'}`}
                                            title={tag}
                                        >
                                            {tag}
                                            <DriftDot drift={driftFor(s.name, img)} />
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

// ── Promotion audit trail + rollback (AB#2092) ────────────────────────────────

function promotionTimeLabel(iso: string): string {
    const d = new Date(iso)
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
}

function PromotionRow({ record, onRollback, isRollingBack }: {
    record: PromotionRecord
    onRollback: (id: string) => void
    isRollingBack: boolean
}) {
    const isRollback = record.rollbackOf !== null
    const reversible = !isRollback && record.imageChanges.some(c => c.beforeTag !== null)
    const changesTitle = record.imageChanges
        .map(c => `${c.image}: ${c.beforeTag ?? '(new)'} → ${c.afterTag}`)
        .join('\n')

    return (
        <div className="flex items-center gap-4 px-4 py-3">
            <span className="w-32 shrink-0 text-xs text-slate-500">{promotionTimeLabel(record.createdAt)}</span>
            <span className="w-28 shrink-0 text-sm font-semibold text-slate-200">
                {isRollback ? record.targetEnv : `${record.sourceEnv} → ${record.targetEnv}`}
            </span>
            <span className="flex items-center gap-1.5">
                {isRollback && (
                    <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-semibold text-rose-400 ring-1 ring-rose-500/30">
                        rollback
                    </span>
                )}
                {record.forced && (
                    <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-400 ring-1 ring-amber-500/30">
                        forced
                    </span>
                )}
            </span>
            <span className="flex-1 truncate text-xs text-slate-500" title={changesTitle}>
                {record.imageChanges.length} image{record.imageChanges.length !== 1 ? 's' : ''}
                {record.gitopsCommitSha ? ` · ${record.gitopsCommitSha.slice(0, 8)}` : ''}
            </span>
            <span className="w-44 truncate text-right text-xs text-slate-500" title={record.actor}>{record.actor}</span>
            {reversible && (
                <button
                    onClick={() => onRollback(record.id)}
                    disabled={isRollingBack}
                    className="shrink-0 rounded-lg border border-rose-800/60 px-2.5 py-1 text-[11px] font-medium text-rose-300 hover:bg-rose-950/50 transition-colors disabled:opacity-40"
                >
                    {isRollingBack ? 'Rolling back…' : 'Roll back'}
                </button>
            )}
        </div>
    )
}

function PromotionHistoryPanel() {
    const queryClient = useQueryClient()
    const { data } = useQuery({
        queryKey: ['promotion-history'],
        queryFn: () => releaseApi.getPromotionHistory(10),
        refetchInterval: 60_000,
        staleTime: 30_000,
    })
    const [rollbackError, setRollbackError] = useState<string | null>(null)
    const [rollbackMessage, setRollbackMessage] = useState<string | null>(null)

    const rollback = useMutation({
        mutationFn: (id: string) => releaseApi.rollbackPromotion(id),
        onMutate: () => { setRollbackError(null); setRollbackMessage(null) },
        onSuccess: (res) => {
            setRollbackMessage(res.verified ? `${res.message} Verified.` : `${res.message} ${res.verificationDetail ?? ''}`.trim())
            void queryClient.invalidateQueries({ queryKey: ['pipeline'] })
            void queryClient.invalidateQueries({ queryKey: ['pipeline-drift'] })
            void queryClient.invalidateQueries({ queryKey: ['promotion-history'] })
        },
        onError: (err) => setRollbackError(err instanceof Error ? err.message : 'Rollback failed'),
    })

    // Hidden entirely when there is no recorded history (e.g. no ops DB configured).
    if (!data || data.promotions.length === 0) return null

    return (
        <div className="mt-10 rounded-xl border border-slate-800 overflow-hidden">
            <div className="bg-slate-900 px-4 py-3">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Promotion history — audit trail &amp; rollback
                </span>
            </div>
            {rollbackMessage && (
                <div className="border-b border-slate-800 bg-emerald-950/30 px-4 py-2 text-xs text-emerald-300">
                    {rollbackMessage}
                </div>
            )}
            {rollbackError && (
                <div className="border-b border-slate-800 bg-red-950/40 px-4 py-2 text-xs text-red-300">
                    {rollbackError}
                </div>
            )}
            <div className="divide-y divide-slate-800">
                {data.promotions.map(p => (
                    <PromotionRow
                        key={p.id}
                        record={p}
                        isRollingBack={rollback.isPending && rollback.variables === p.id}
                        onRollback={(id) => {
                            if (window.confirm('Roll this promotion back to its recorded before-tags?')) {
                                rollback.mutate(id)
                            }
                        }}
                    />
                ))}
            </div>
        </div>
    )
}

function PipelineSkeleton() {
    return (
        <div className="flex items-start gap-4">
            {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                    <div className="h-52 w-44 rounded-xl tag-shimmer opacity-20" />
                    {i < 4 && <div className="h-0.5 w-16 tag-shimmer opacity-20" />}
                </div>
            ))}
        </div>
    )
}

// ── CI/CD build panel ─────────────────────────────────────────────────────────

function RunHistoryRow({ run }: { run: PipelineRunInfo }) {
    const badge = runBadge(run)
    return (
        <div className="flex items-center gap-3 py-1.5 pl-8 border-t border-slate-800/50">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.bg} ${badge.text}`}>
                {badge.label}
            </span>
            <span className="text-[11px] text-slate-500 w-20 text-right">{relativeTime(run.finishedAt)}</span>
            {run.webUrl ? (
                <a
                    href={run.webUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-600 hover:text-slate-400 transition-colors"
                >
                    <svg className="h-3 w-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M6 3H3a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-3" strokeLinecap="round" />
                        <path d="M9 1h6m0 0v6m0-6L7 9" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </a>
            ) : (
                <span className="w-3" />
            )}
        </div>
    )
}

function RunHistoryPanel({ deployable }: { deployable: string }) {
    const { data, isLoading } = useQuery({
        queryKey: ['pipeline-run-history', deployable],
        queryFn: () => releaseApi.getPipelineRunHistory(deployable, 8),
        staleTime: 30_000,
    })

    if (isLoading) {
        return <div className="pl-8 py-2 text-xs text-slate-500 animate-pulse">Loading history…</div>
    }
    if (!data?.length) {
        return <div className="pl-8 py-2 text-xs text-slate-600">No history available</div>
    }

    return (
        <div className="bg-slate-900/40">
            {data.slice(1).map((run, i) => (
                <RunHistoryRow key={i} run={run} />
            ))}
        </div>
    )
}

function CiBuildPanel() {
    const [expanded, setExpanded] = useState<string | null>(null)

    const { data, isLoading, isError } = useQuery({
        queryKey: ['pipeline-runs'],
        queryFn: releaseApi.getPipelineRuns,
        refetchInterval: 30_000,
        staleTime: 20_000,
    })

    if (isLoading) return null
    if (isError || !data) return null

    return (
        <div className="mt-10 rounded-xl border border-slate-800 overflow-hidden">
            <div className="bg-slate-900 px-4 py-3 flex items-center gap-2">
                <svg className="h-4 w-4 text-slate-400" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M2 8a6 6 0 1 0 12 0A6 6 0 0 0 2 8Z" />
                    <path d="M8 5v3l2 1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">CI/CD builds — latest run per pipeline</span>
            </div>
            <div className="divide-y divide-slate-800">
                {data.runs.map(run => {
                    const badge = runBadge(run)
                    const isExpanded = expanded === run.deployable
                    return (
                        <div key={run.deployable}>
                            <div className="flex items-center gap-4 px-4 py-3">
                                <button
                                    onClick={() => setExpanded(isExpanded ? null : run.deployable)}
                                    className="text-slate-600 hover:text-slate-400 transition-colors shrink-0"
                                    title={isExpanded ? 'Hide history' : 'Show history'}
                                >
                                    <svg
                                        className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                        viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"
                                    >
                                        <path d="M6 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <span className="w-28 text-sm font-semibold text-slate-200 truncate">{run.deployable}</span>
                                <span className="flex-1 text-xs font-mono text-slate-500 truncate">{run.pipelineName}</span>
                                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${badge.bg} ${badge.text}`}>
                                    {badge.label}
                                </span>
                                <span className="w-20 text-right text-xs text-slate-600">{relativeTime(run.finishedAt)}</span>
                                {run.webUrl ? (
                                    <a
                                        href={run.webUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-slate-500 hover:text-slate-300 transition-colors"
                                        title="Open in Azure DevOps"
                                    >
                                        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <path d="M6 3H3a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-3" strokeLinecap="round" />
                                            <path d="M9 1h6m0 0v6m0-6L7 9" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </a>
                                ) : (
                                    <span className="w-3.5" />
                                )}
                            </div>
                            {isExpanded && <RunHistoryPanel deployable={run.deployable} />}
                        </div>
                    )
                })}
                {data.runs.length === 0 && (
                    <p className="px-4 py-6 text-sm text-slate-500 text-center">No pipeline runs configured.</p>
                )}
            </div>
        </div>
    )
}
